import React, { useState } from "react";
import { ShieldCheck, ShieldQuestion, Check, X, Info } from "lucide-react";
import { computeTrustBadge } from "../../lib/trustBadge";

/**
 * TrustBadge — gold / silver / bronze pill shown when a startup's KYC has
 * been ADMIN-APPROVED and at least one of (Aadhaar / CIN / GSTIN) is
 * externally verified. Hover / tap the badge to see per-check breakdown.
 *
 * Usage: <TrustBadge startup={obj} />                — accept full startup doc
 *        <TrustBadge badge={precomputed} />          — pass server-computed
 *        <TrustBadge {...} size="sm" | "md" | "lg" />
 *        <TrustBadge {...} inline />                 — pill-only, no tooltip UI
 *
 * If no badge should show (no approval or 0 checks), the component renders
 * NOTHING (returns null) — never a "not verified" pill, per product rule.
 */

const TIER_CLASSES = {
  gold: {
    pill:  "bg-gradient-to-r from-[#F5E7A6] via-[#F1D97C] to-[#F5E7A6] text-[#5C4400] border border-[#B98A00] shadow-sm",
    ring:  "ring-2 ring-[#F1D97C]/40",
    dot:   "text-[#B98A00]",
    tooltipHead: "text-[#5C4400]",
  },
  silver: {
    pill:  "bg-gradient-to-r from-[#E6E9EE] via-[#D9DEE4] to-[#E6E9EE] text-ink border border-[#8A96A2] shadow-sm",
    ring:  "ring-2 ring-[#D9DEE4]/40",
    dot:   "text-[#6E7C87]",
    tooltipHead: "text-ink",
  },
  bronze: {
    pill:  "bg-gradient-to-r from-[#F0DFC6] via-[#E9C79A] to-[#F0DFC6] text-[#4B2E10] border border-[#8C5A26] shadow-sm",
    ring:  "ring-2 ring-[#E9C79A]/40",
    dot:   "text-[#8C5A26]",
    tooltipHead: "text-[#4B2E10]",
  },
};

const SIZE_CLASSES = {
  sm: { pill: "text-[10px] px-2 py-0.5 gap-1", icon: 10 },
  md: { pill: "text-xs px-2.5 py-1 gap-1.5", icon: 12 },
  lg: { pill: "text-sm px-3 py-1.5 gap-2", icon: 14 },
};

const CHECK_LABEL = {
  aadhaar: "Aadhaar (DigiLocker)",
  cin:     "CIN (MCA)",
  gstin:   "GSTIN (GSTN)",
};

export default function VerifiedBadge({
  startup, badge, size = "md", inline = false, testId,
}) {
  const b = badge || computeTrustBadge(startup);
  const [open, setOpen] = useState(false);

  if (!b?.tier) return null;

  const cls = TIER_CLASSES[b.tier];
  const sz = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  const pill = (
    <span
      className={`inline-flex items-center rounded-full font-heading font-semibold whitespace-nowrap ${cls.pill} ${sz.pill}`}
      data-testid={testId || `verified-badge-${b.tier}`}
      data-tier={b.tier}
      title={b.label}
    >
      <ShieldCheck size={sz.icon} className={cls.dot} />
      <span>{b.label}</span>
      {size !== "sm" && (
        <span className={`text-[10px] font-mono opacity-80 ml-0.5`}>{b.count}/3</span>
      )}
    </span>
  );

  if (inline) return pill;

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
    >
      <button type="button" className="inline-block outline-none focus-visible:ring-2 focus-visible:ring-gold/50 rounded-full">
        {pill}
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 top-full mt-2 w-72 bg-white border border-navy/10 rounded-lg shadow-xl p-3 text-xs text-ink"
          data-testid="verified-badge-tooltip"
          role="tooltip"
        >
          <div className={`text-[10px] font-mono uppercase tracking-widest ${cls.tooltipHead}`}>
            {b.tier.toUpperCase()} · {b.label}
          </div>
          <div className="text-xs text-ink-soft mt-1 leading-relaxed">
            {b.tier === "gold"
              ? "This startup has completed all three external verifications and passed VenturaZ admin review."
              : b.tier === "silver"
              ? "Two of three external verifications completed. Admin has approved KYC."
              : "One external verification completed. Admin has approved KYC."}
          </div>
          <ul className="mt-3 space-y-1.5" data-testid="verified-badge-checks">
            {["aadhaar", "cin", "gstin"].map((k) => (
              <li key={k} className="flex items-center gap-1.5">
                {b.checks?.[k] ? (
                  <Check size={12} className="text-emerald-600 shrink-0" />
                ) : (
                  <X size={12} className="text-ink-muted/60 shrink-0" />
                )}
                <span className={b.checks?.[k] ? "text-ink" : "text-ink-muted line-through"}>
                  {CHECK_LABEL[k]}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-2 border-t border-navy/[0.06] text-[10px] text-ink-muted flex items-center gap-1">
            <Info size={11} /> Verifications are re-checked every 90 days.
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * VerifiedBadgePlaceholder — shows a subtle "not yet verified" chip. Optional —
 * pages that want to visibly nudge founders to complete KYC can render this
 * when computeTrustBadge returns tier=null.
 */
export function VerifiedBadgePlaceholder({ size = "md" }) {
  const sz = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  return (
    <span
      className={`inline-flex items-center rounded-full font-mono text-ink-muted bg-navy/[0.04] border border-navy/10 ${sz.pill}`}
      data-testid="verified-badge-none"
    >
      <ShieldQuestion size={sz.icon} className="text-ink-muted/70" />
      <span className="uppercase tracking-widest">Verification pending</span>
    </span>
  );
}
